/**
 * The CollisionShapeContainer holds all collision shapes for a given room or the world in case of the global
 * container, as well as the collision shapes that are active collision in the respective context.
 *
 * The container also allows other containers to temporarily attach to this container. In this case the attached
 * container forwards add and remove calls to this container. This is necessary because the utility room holds models
 * that can persist over room changes and thus need to be treated separately.
 *
 * By allowing to attach containers we can combine the collision shapes of the current active room with the collision
 * shapes of the utility room while still having the ability to easily switch out collision shapes of the rooms on room
 * change.
 */
export class CollisionShapeContainer {
    constructor() {
        this.allCollisionShapes = new Map();
        this.activeCollisionShapes = new Set();
        this.triggerCollisionShapes = new Set();
    }
    /// Getter and setter ///
    getCollisionShape(uid) {
        return this.allCollisionShapes.get(uid);
    }
    getAllCollisionShapes() {
        return Array.from(this.allCollisionShapes.values());
    }
    /** Read-only snapshots are shared across queries until container membership changes. */
    getActiveCollisionShapes(includeTriggers) {
        if (includeTriggers) {
            if (this.activeSnapshot === undefined) {
                this.activeSnapshot = [...this.activeCollisionShapes, ...this.triggerCollisionShapes];
            }
            return this.activeSnapshot;
        }
        if (this.selectableSnapshot === undefined) {
            this.selectableSnapshot = Array.from(this.activeCollisionShapes);
        }
        return this.selectableSnapshot;
    }
    isEmpty() {
        return this.allCollisionShapes.size === 0;
    }
    /// Collision shape methods ///
    addCollisionShape(shape) {
        this.invalidateSnapshots();
        this.allCollisionShapes.set(shape.uid, shape);
        if (shape.isSelectable)
            this.activeCollisionShapes.add(shape);
        else if (shape.isTrigger)
            this.triggerCollisionShapes.add(shape);
        // Forward to parent collision container
        if (this.parent !== undefined)
            this.parent.addCollisionShape(shape);
    }
    updateCollisionShape(shape) {
        this.invalidateSnapshots();
        if (shape.isSelectable)
            this.activeCollisionShapes.add(shape);
        else
            this.activeCollisionShapes.delete(shape);
        if (shape.isTrigger && !shape.isSelectable)
            this.triggerCollisionShapes.add(shape);
        else
            this.triggerCollisionShapes.delete(shape);
        // Forward to parent collision container
        if (this.parent !== undefined)
            this.parent.updateCollisionShape(shape);
    }
    removeCollisionShape(shape) {
        this.invalidateSnapshots();
        this.allCollisionShapes.delete(shape.uid);
        this.activeCollisionShapes.delete(shape);
        this.triggerCollisionShapes.delete(shape);
        // Forward to parent collision container
        if (this.parent !== undefined)
            this.parent.removeCollisionShape(shape);
    }
    clear() {
        // The parent must still be able to see which shapes are being removed.
        if (this.parent !== undefined)
            this.parent.removeCollisionShapesOfContainer(this);
        this.invalidateSnapshots();
        for (const collisionShape of this.allCollisionShapes.values()) {
            collisionShape.destroy();
        }
        this.allCollisionShapes.clear();
        this.activeCollisionShapes.clear();
        this.triggerCollisionShapes.clear();
    }
    /// Container methods ///
    attachContainer(container) {
        this.addCollisionShapesOfContainer(container);
        container.parent = this;
    }
    detachContainer(container) {
        this.removeCollisionShapesOfContainer(container);
        container.parent = undefined;
    }
    /// Private helper methods ///
    addCollisionShapesOfContainer(container) {
        this.invalidateSnapshots();
        for (const shape of container.allCollisionShapes.values()) {
            this.allCollisionShapes.set(shape.uid, shape);
        }
        for (const shape of container.activeCollisionShapes.values()) {
            this.activeCollisionShapes.add(shape);
        }
        for (const shape of container.triggerCollisionShapes.values()) {
            this.triggerCollisionShapes.add(shape);
        }
    }
    removeCollisionShapesOfContainer(container) {
        this.invalidateSnapshots();
        for (const shape of container.allCollisionShapes.values()) {
            this.allCollisionShapes.delete(shape.uid);
        }
        for (const shape of container.activeCollisionShapes.values()) {
            this.activeCollisionShapes.delete(shape);
        }
        for (const shape of container.triggerCollisionShapes.values()) {
            this.triggerCollisionShapes.delete(shape);
        }
    }
    invalidateSnapshots() {
        this.selectableSnapshot = undefined;
        this.activeSnapshot = undefined;
    }
}
